"use client";

import React from "react";
import { useAppContext } from "@/contexts/app-context";
import { $api, apiAuth } from "@/openapi/client-auth";
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@donista/shared/api/generated";

type ShopDto = components["schemas"]["ShopDto"];

const QUERY_KEY = ["get", "/users/recently-viewed-shops"] as const;

type RecentlyViewedContextValue = {
  recentShops: ShopDto[];
  addRecentShop: (shopId: string) => void;
  isLoading: boolean;
};

const RecentlyViewedContext = React.createContext<RecentlyViewedContextValue | undefined>(undefined);

export function RecentlyViewedProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAppContext();
  const queryClient = useQueryClient();

  const { data, isLoading } = $api.useQuery(
    "get",
    "/users/recently-viewed-shops",
    {},
    { enabled: !!user && !user.anonymous },
  );
  const recentShops = (user && !user.anonymous) ? (data ?? []) : [];

  const lastAddedRef = React.useRef<string | null>(null);

  const addRecentShop = React.useCallback(
    async (shopId: string) => {
      if (!user || user.anonymous) return;
      if (lastAddedRef.current === shopId) return;
      lastAddedRef.current = shopId;
      const id = crypto.randomUUID();
      await apiAuth.POST("/users/recently-viewed-shops", {
        body: { id, shopId },
      });
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY] });
    },
    [user, queryClient],
  );

  return (
    <RecentlyViewedContext.Provider value={{ recentShops, addRecentShop, isLoading }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export function useRecentlyViewedShops() {
  const ctx = React.useContext(RecentlyViewedContext);
  if (!ctx) {
    throw new Error("useRecentlyViewedShops must be used within RecentlyViewedProvider");
  }
  return ctx;
}
